import { Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import ArticleCard from "@/components/base/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import BackToTop from "@/components/feature/BackToTop";
import PageMeta from "@/components/feature/PageMeta";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import { articles } from "@/mocks/articles";
import { topics } from "@/mocks/topics";
import { formatDate, formatViews } from "@/utils/format";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-4 md:px-6 pt-32 pb-24 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
            这篇内容走丢了
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            它可能已被调整或下架，回首页看看今天的热榜吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = articles
    .filter((item) => item.id !== article.id && item.categoryId === article.categoryId)
    .concat(articles.filter((item) => item.id !== article.id))
    .filter(
      (item, index, self) => self.findIndex((x) => x.id === item.id) === index,
    )
    .slice(0, 3);

  const ranked = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: article.cover,
        datePublished: article.date,
        dateModified: article.date,
        articleSection: article.categoryName,
        keywords: article.tags.join(","),
        inLanguage: "zh-CN",
        author: { "@type": "Person", name: article.author },
        publisher: {
          "@type": "Organization",
          name: "热瓜前线",
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://rnmka.cn/article/${article.id}`,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://rnmka.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: article.categoryName,
            item: `https://rnmka.cn/topic/${article.categoryId}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: article.title,
            item: `https://rnmka.cn/article/${article.id}`,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title={`${article.title}｜热瓜前线`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},${article.categoryName},爆料看料,热点观察`}
        path={`/article/${article.id}`}
        type="article"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main>
        <article className="pt-28 md:pt-32">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: article.categoryName, to: `/topic/${article.categoryId}` },
                { label: "正文" },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 mt-6">
            <Link
              to={`/topic/${article.categoryId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-label hover:bg-accent-200 transition-colors cursor-pointer"
            >
              <i className="ri-hashtag w-3.5 h-3.5 flex items-center justify-center"></i>
              {article.categoryName}
            </Link>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500 font-label">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-3-line w-4 h-4 flex items-center justify-center"></i>
                {article.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {formatDate(article.date)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                阅读约 {article.readMinutes} 分钟
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                {formatViews(article.views)} 次围观
              </span>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 mt-8">
            <div className="w-full h-56 md:h-96 rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - ${article.categoryName}`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8">
              <div className="flex flex-col gap-5">
                {article.body.map((paragraph, index) => (
                  <Fragment key={index}>
                    <p className="text-base md:text-lg text-foreground-700 leading-loose">
                      {paragraph}
                    </p>
                    {index === 1 && (
                      <blockquote className="my-2 border-l-4 border-primary-400 bg-primary-50/70 rounded-r-lg px-5 py-4">
                        <p className="font-heading text-lg md:text-xl text-foreground-900 leading-relaxed">
                          {article.quote}
                        </p>
                      </blockquote>
                    )}
                  </Fragment>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-background-200/80 flex flex-wrap items-center gap-3">
                <span className="text-sm font-label text-foreground-500">标签</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topic/${article.categoryId}`}
                    className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs font-label text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg bg-background-100 border border-background-200/80 p-5 lg:sticky lg:top-24">
                <h2 className="font-heading font-bold text-base text-foreground-900 inline-flex items-center gap-2">
                  <i className="ri-fire-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  继续围观
                </h2>
                <ol className="mt-4 flex flex-col gap-3">
                  {ranked.map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/article/${item.id}`}
                        className="group flex items-start gap-3 cursor-pointer"
                      >
                        <span
                          className={`w-5 h-5 shrink-0 flex items-center justify-center rounded text-xs font-label font-bold ${
                            index < 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-300 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
                <Link
                  to="/#subscribe"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-notification-3-line w-4 h-4 flex items-center justify-center"></i>
                  订阅每日精选
                </Link>
              </div>
            </aside>
          </div>
        </article>

        <section className="py-12 md:py-16 bg-background-100 mt-12 md:mt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              相关推荐
            </h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}